/**
 * TaskApprovalPolicyManager - Visual Policy Builder for Task Approval
 *
 * FERP CRITICAL: Component for creating and managing Task approval policies
 *
 * FERP Principles:
 * - Data-driven: All policies stored in DynamoDB via PolicyRepository
 * - Repository pattern: Uses PolicyRepository for all data access
 * - Single responsibility: Task approval policy management only
 * - Type-safe: Full TypeScript types
 * - Life-critical: Complete error handling with user notifications
 * - Domain-agnostic: Works for any Task type
 * - Mantine UI: ONLY Mantine components (no MUI)
 *
 * Features:
 * - Visual policy builder with condition editor
 * - Dry-run testing before saving
 * - Policy list with enable/disable toggle
 * - Priority management (higher = evaluated first)
 * - Complete CRUD operations
 *
 * Usage:
 * ```tsx
 * <TaskApprovalPolicyManager tenantId="tenant-123" />
 * ```
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  TextInput,
  NumberInput,
  Select,
  Switch,
  Badge,
  Table,
  ActionIcon,
  Loader,
  Alert,
  Modal,
  Tabs,
  JsonInput,
  Code,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconTestPipe,
  IconPlayerPlay,
  IconAlertCircle,
  IconShieldCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useGovernance } from '../../governance/hooks/useGovernance';
import type { PolicyRule, PolicyCondition, PolicyAction } from '../../governance/hooks/useGovernance';

// ============================================================================
// TypeScript Types
// ============================================================================

interface TaskApprovalPolicyManagerProps {
  tenantId: string;
}

interface PolicyFormData {
  policyName: string;
  priority: number;
  enabled: boolean;
  conditions: PolicyCondition[];
  actions: PolicyAction[];
}

const INITIAL_FORM_DATA: PolicyFormData = {
  policyName: '',
  priority: 10,
  enabled: true,
  conditions: [],
  actions: [],
};

// ============================================================================
// TaskApprovalPolicyManager Component
// ============================================================================

export const TaskApprovalPolicyManager: React.FC<TaskApprovalPolicyManagerProps> = ({
  tenantId,
}) => {
  const {
    getPolicyRules,
    createPolicyRule,
    updatePolicyRule,
    deletePolicyRule,
    dryRunPolicy,
  } = useGovernance();

  const [policies, setPolicies] = useState<PolicyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<PolicyRule | null>(null);
  const [formData, setFormData] = useState<PolicyFormData>(INITIAL_FORM_DATA);
  const [dryRunTestData, setDryRunTestData] = useState('{\n  "priority": "high",\n  "isCritical": true\n}');
  const [dryRunResult, setDryRunResult] = useState<any>(null);

  // Load policies on mount
  useEffect(() => {
    loadPolicies();
  }, [tenantId]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const result = await getPolicyRules('task_approval');
      setPolicies(result);
    } catch (error) {
      console.error('[TaskApprovalPolicyManager] Load error:', error);
      notifications.show({
        title: 'Error Loading Policies',
        message: 'Failed to load Task approval policies. Please try again.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = () => {
    setEditingPolicy(null);
    setFormData(INITIAL_FORM_DATA);
    setDryRunResult(null);
    setIsModalOpen(true);
  };

  const handleEditPolicy = (policy: PolicyRule) => {
    setEditingPolicy(policy);
    setFormData({
      policyName: policy.policy_name,
      priority: policy.priority,
      enabled: policy.enabled,
      conditions: policy.conditions,
      actions: policy.actions,
    });
    setDryRunResult(null);
    setIsModalOpen(true);
  };

  const handleSavePolicy = async () => {
    try {
      if (editingPolicy) {
        // Update existing policy
        await updatePolicyRule({
          policyId: editingPolicy.policy_id,
          policyName: formData.policyName,
          conditions: formData.conditions,
          actions: formData.actions,
          priority: formData.priority,
          enabled: formData.enabled,
        });
        notifications.show({
          title: 'Policy Updated',
          message: `Policy "${formData.policyName}" has been updated successfully.`,
          color: 'green',
        });
      } else {
        // Create new policy
        await createPolicyRule({
          policyType: 'task_approval',
          policyName: formData.policyName,
          conditions: formData.conditions,
          actions: formData.actions,
          priority: formData.priority,
          enabled: formData.enabled,
        });
        notifications.show({
          title: 'Policy Created',
          message: `Policy "${formData.policyName}" has been created successfully.`,
          color: 'green',
        });
      }

      setIsModalOpen(false);
      await loadPolicies();
    } catch (error) {
      console.error('[TaskApprovalPolicyManager] Save error:', error);
      notifications.show({
        title: 'Error Saving Policy',
        message: 'Failed to save policy. Please try again.',
        color: 'red',
      });
    }
  };

  const handleDeletePolicy = async (policyId: string, policyName: string) => {
    if (!confirm(`Are you sure you want to delete policy "${policyName}"?`)) {
      return;
    }

    try {
      await deletePolicyRule(policyId);
      notifications.show({
        title: 'Policy Deleted',
        message: `Policy "${policyName}" has been deleted.`,
        color: 'green',
      });
      await loadPolicies();
    } catch (error) {
      console.error('[TaskApprovalPolicyManager] Delete error:', error);
      notifications.show({
        title: 'Error Deleting Policy',
        message: 'Failed to delete policy. Please try again.',
        color: 'red',
      });
    }
  };

  const handleDryRun = async () => {
    if (!editingPolicy && formData.conditions.length === 0) {
      notifications.show({
        title: 'No Conditions',
        message: 'Please add at least one condition before testing.',
        color: 'yellow',
      });
      return;
    }

    try {
      const testData = JSON.parse(dryRunTestData);
      const policyId = editingPolicy?.policy_id || 'temp-policy-id';

      const result = await dryRunPolicy(policyId, testData);

      setDryRunResult(result);

      if (result) {
        notifications.show({
          title: 'Dry-Run Complete',
          message: result.matched ? 'Policy matched!' : 'Policy did not match.',
          color: result.matched ? 'green' : 'yellow',
        });
      }
    } catch (error) {
      console.error('[TaskApprovalPolicyManager] Dry-run error:', error);
      notifications.show({
        title: 'Dry-Run Error',
        message: 'Failed to run policy test. Check test data JSON format.',
        color: 'red',
      });
    }
  };

  const addCondition = () => {
    setFormData({
      ...formData,
      conditions: [
        ...formData.conditions,
        { field: 'priority', operator: 'equals', value: '' } as PolicyCondition,
      ],
    });
  };

  const updateCondition = (index: number, updates: Partial<PolicyCondition>) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], ...updates } as PolicyCondition;
    setFormData({ ...formData, conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter((_: any, i) => i !== index),
    });
  };

  const addAction = () => {
    setFormData({
      ...formData,
      actions: [
        ...formData.actions,
        { type: 'approve', params: {} } as PolicyAction,
      ],
    });
  };

  const updateAction = (index: number, updates: Partial<PolicyAction>) => {
    const newActions = [...formData.actions];
    newActions[index] = { ...newActions[index], ...updates } as PolicyAction;
    setFormData({ ...formData, actions: newActions });
  };

  const removeAction = (index: number) => {
    setFormData({
      ...formData,
      actions: formData.actions.filter((_: any, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <Container size="xl" mt="xl">
        <Group justify="center">
          <Loader size="lg" />
          <Text>Loading Task approval policies...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" mt="xl">
      <Group justify="space-between" mb="xl">
        <div>
          <Title order={2}>Task Approval Policy Manager</Title>
          <Text c="dimmed" size="sm" mt="xs">
            Create and manage data-driven approval policies for Task submission.
            All policies are evaluated in priority order (highest first).
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={handleCreatePolicy}
        >
          Create Policy
        </Button>
      </Group>

      {policies.length === 0 ? (
        <Alert color="blue" icon={<IconAlertCircle />} title="No Policies Configured">
          No Task approval policies found. Create your first policy to define approval routing rules.
          <br />
          <strong>Default behavior:</strong> All Tasks will auto-approve without manual review.
        </Alert>
      ) : (
        <Paper withBorder p="md">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Policy Name</Table.Th>
                <Table.Th>Conditions</Table.Th>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {policies
                .sort((a, b) => b.priority - a.priority)
                .map((policy) => (
                  <Table.Tr key={policy.policy_id}>
                    <Table.Td>
                      <Badge size="lg">{policy.priority}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{policy.policy_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {policy.conditions.length} condition(s)
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {policy.actions.map((action, i) => (
                          <Badge key={i} size="sm" variant="light">
                            {action.type}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {policy.enabled ? (
                        <Badge color="green">Enabled</Badge>
                      ) : (
                        <Badge color="gray">Disabled</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ActionIcon
                          variant="light"
                          onClick={() => handleEditPolicy(policy)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="light"
                          color="red"
                          onClick={() => handleDeletePolicy(policy.policy_id, policy.policy_name)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Policy Editor Modal */}
      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPolicy ? 'Edit Policy' : 'Create New Policy'}
        size="xl"
      >
        <Tabs defaultValue="basic">
          <Tabs.List>
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="conditions">Conditions</Tabs.Tab>
            <Tabs.Tab value="actions">Actions</Tabs.Tab>
            <Tabs.Tab value="test" leftSection={<IconTestPipe size={14} />}>
              Test Policy
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="basic" pt="md">
            <Stack>
              <TextInput
                label="Policy Name"
                placeholder="High Priority Auto-Approve"
                value={formData.policyName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, policyName: e.target.value })}
                required
              />
              <NumberInput
                label="Priority"
                description="Higher priority policies are evaluated first"
                value={formData.priority}
                onChange={(value: string | number) => setFormData({ ...formData, priority: value as number })}
                min={1}
                max={100}
              />
              <Switch
                label="Enabled"
                description="Disabled policies are not evaluated"
                checked={formData.enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, enabled: e.currentTarget.checked })}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="conditions" pt="md">
            <Stack>
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Policy Conditions (ALL must match)
                </Text>
                <Button size="xs" onClick={addCondition} leftSection={<IconPlus size={14} />}>
                  Add Condition
                </Button>
              </Group>

              {formData.conditions.map((condition, index) => (
                <Paper key={index} withBorder p="sm">
                  <Group ta="right">
                    <TextInput
                      label="Field"
                      placeholder="priority"
                      value={condition.field}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCondition(index, { field: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <Select
                      label="Operator"
                      data={[
                        { value: 'equals', label: 'Equals' },
                        { value: 'not_equals', label: 'Not Equals' },
                        { value: 'greater_than', label: 'Greater Than' },
                        { value: 'less_than', label: 'Less Than' },
                        { value: 'contains', label: 'Contains' },
                        { value: 'regex', label: 'Regex' },
                      ]}
                      value={condition.operator}
                      onChange={(value: string | null) => updateCondition(index, { operator: value as any })}
                      style={{ flex: 1 }}
                    />
                    <TextInput
                      label="Value"
                      placeholder="high"
                      value={String(condition.value)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateCondition(index, { value: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <ActionIcon color="red" onClick={() => removeCondition(index)}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                </Paper>
              ))}

              {formData.conditions.length === 0 && (
                <Alert color="yellow">
                  No conditions defined. Policy will match all Tasks (not recommended).
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="actions" pt="md">
            <Stack>
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Policy Actions (Executed when policy matches)
                </Text>
                <Button size="xs" onClick={addAction} leftSection={<IconPlus size={14} />}>
                  Add Action
                </Button>
              </Group>

              {formData.actions.map((action, index) => (
                <Paper key={index} withBorder p="sm">
                  <Stack>
                    <Group ta="right">
                      <Select
                        label="Action Type"
                        data={[
                          { value: 'approve', label: 'Approve' },
                          { value: 'block', label: 'Block' },
                          { value: 'escalate', label: 'Escalate' },
                          { value: 'notify', label: 'Notify' },
                          { value: 'custom', label: 'Custom' },
                        ]}
                        value={action.type}
                        onChange={(value: string | null) => updateAction(index, { type: value as any })}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon color="red" onClick={() => removeAction(index)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                    <JsonInput
                      label="Action Parameters"
                      placeholder='{"approvers": ["manager", "cfo"]}'
                      value={JSON.stringify(action.params || {}, null, 2)}
                      onChange={(value) => {
                        try {
                          const params = JSON.parse(value);
                          updateAction(index, { params });
                        } catch (e) {
                          // Invalid JSON, ignore
                        }
                      }}
                      minRows={3}
                    />
                  </Stack>
                </Paper>
              ))}

              {formData.actions.length === 0 && (
                <Alert color="yellow">
                  No actions defined. Policy will have no effect.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="test" pt="md">
            <Stack>
              <Alert color="blue" icon={<IconTestPipe />}>
                Test your policy against sample Task data without saving.
                This uses Zen Engine evaluation to verify conditions work correctly.
              </Alert>

              <JsonInput
                label="Test Data"
                description="Enter Task data to test against this policy"
                placeholder='{"priority": "high", "isCritical": true}'
                value={dryRunTestData}
                onChange={setDryRunTestData}
                minRows={6}
              />

              <Button
                leftSection={<IconPlayerPlay size={16} />}
                onClick={handleDryRun}
                disabled={!editingPolicy && formData.conditions.length === 0}
              >
                Run Test
              </Button>

              {dryRunResult && (
                <Paper withBorder p="md" style={{
                  borderColor: dryRunResult.matched
                    ? 'var(--mantine-color-green-6)'
                    : 'var(--mantine-color-gray-4)'
                }}>
                  <Stack>
                    <Group>
                      <IconShieldCheck
                        size={24}
                        color={dryRunResult.matched
                          ? 'var(--mantine-color-green-6)'
                          : 'var(--mantine-color-gray-6)'}
                      />
                      <Text fw={600} size="lg">
                        {dryRunResult.matched ? 'Policy Matched ✓' : 'Policy Did Not Match'}
                      </Text>
                    </Group>

                    <Code block>{JSON.stringify(dryRunResult, null, 2)}</Code>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="xl">
          <Button variant="default" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePolicy}
            disabled={!formData.policyName || formData.conditions.length === 0 || formData.actions.length === 0}
          >
            {editingPolicy ? 'Update Policy' : 'Create Policy'}
          </Button>
        </Group>
      </Modal>
    </Container>
  );
};

export default TaskApprovalPolicyManager;
